/**
 * NovaVersaoAviso — a notícia de que saiu versão nova, sem sequestrar ninguém.
 *
 * Histórico. 14/set: o Mac da Dra. tinha uma aba aberta desde 09/09 e três
 * conversas com a Essentia saíram por ela, com código velho. Daí nasceu a
 * pílula no topo com o botão "Recarregar". Só que o botão passou a aparecer em
 * TODO deploy, em qualquer tela (Valdair, 19/09): "não obrigar o reload a todo
 * momento... deveria ser apenas informação que fica por um tempo e depois some".
 *
 * Como ficou:
 *   • O botão Recarregar NASCE DESLIGADO. Ele só aparece quando o deploy pede,
 *     pela linha "recarregar: sim - <motivo>" da NOTA-DA-VERSAO.md. Quem decide
 *     é quem faz o deploy, não a tela (migration 252).
 *   • No modo normal o aviso só informa: a frase aparece, fica ~30 s (15 s em
 *     correção) e SAI. Nada fica boiando depois (Valdair, 21/09: "não deveria
 *     ficar aparecendo esse V boiando em cima quando não tem mensagem nenhuma;
 *     você já tem o V do lado, abaixo do nome"). Quem quer a versão lê no
 *     rodapé do menu, que mostra "v1.0.X · Atualizações" e acende quando há
 *     novidade. A barrinha embaixo mostra que a frase vai sumir.
 *   • O que aparece é sempre a novidade mais recente que a pessoa não viu
 *     (migrations 254/255/256): trabalho novo nunca fica atrás de nota velha.
 *   • Aparece na TROCA DE TELA (21/09): logo depois de a pessoa abrir uma
 *     página, que é quando ela está atenta e ainda não mergulhou em nada. Quem
 *     fica muito tempo numa tela só recebe depois de 10 min, no primeiro
 *     movimento. Se a pessoa estiver digitando ou gravando, ele espera terminar.
 *   • O relógio pausa com o mouse em cima, com a janela atrás de outra ou com a
 *     aba escondida. Se a pessoa sumir por mais de 30 s, o tempo recomeça — ela
 *     volta e a notícia ainda está lá.
 *   • Não bloqueia nada: o aviso não recebe clique (pointer-events: none); só o
 *     link e os botões recebem.
 *   • Deploy marcado como "interno"/--sem-nota não aparece para ninguém.
 *
 * Fica flutuando no topo, centralizado, como desde 16/set — não empurra o
 * conteúdo de propósito: empurrar move a tela inteira embaixo do cursor no meio
 * de um clique, que é justamente o que não pode acontecer.
 *
 * A mecânica de versão (versao.json, foco, 5 min) mora em hooks/useVersaoPortal.
 * No local: ?simular-versao=1 (modo normal) e ?simular-versao=recarregar.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { RefreshCw, X, AlertTriangle } from 'lucide-react';
import { theme } from '../../styles/GlobalStyle';
import { motivoDaRecarga, useVersaoPortal } from '../../hooks/useVersaoPortal';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { getLiveRecordingInfo } from '../../hooks/admin/useAudioRecorder';
import { useNovidadeDaHora, msAteVirarAHora } from '../../hooks/useNovidadeDaHora';

/** quanto tempo a notícia fica na tela, por tipo de deploy */
const DURACAO_MS: Record<string, number> = { correcao: 15_000, seguranca: 30_000 };
const DURACAO_PADRAO_MS = 30_000;
/** no modo alerta (aba velha) o aviso fica de pé por isto antes de desistir */
const JANELA_MS = 60 * 60 * 1000;
/** depois do primeiro movimento, ainda espera isto (não pula na mão de ninguém) */
const ESPERA_POS_MOVIMENTO_MS = 2_000;
/** "acabou de trocar de tela" vale por isto: é o melhor momento para aparecer */
const JANELA_TROCA_MS = 5_000;
/** depois da troca, espera a página nova assentar antes de aparecer */
const ESPERA_POS_TROCA_MS = 1_500;
/** quem fica muito tempo numa tela só recebe no primeiro movimento depois disto */
const MESMA_TELA_MS = 10 * 60 * 1000;
/** voltou depois de mais de isto pausado: o tempo recomeça do zero */
const AUSENCIA_REINICIA_MS = 30_000;
/** X no modo "recarregar": silencia nesta aba por 8 h, mesmo com 10 deploys */
const SILENCIO_MS = 8 * 60 * 60 * 1000;
/** de quanto em quanto tempo reconfere se a pessoa parou de digitar/gravar */
const RECHECA_OCUPADO_MS = 1_500;

const TEXTOS = {
  pt: { msg: 'Novidade no portal', aviso: 'Esta aba está desatualizada', btn: 'Recarregar', fechar: 'Fechar', ver: 'ver em atualizações',
        recargaSemMotivo: 'Há uma atualização importante do portal. Recarregue para continuar.' },
  en: { msg: 'New in the portal', aviso: 'This tab is out of date', btn: 'Reload', fechar: 'Close', ver: 'see in updates',
        recargaSemMotivo: 'There is an important portal update. Please reload to continue.' },
  es: { msg: 'Novedad en el portal', aviso: 'Esta pestaña está desactualizada', btn: 'Recargar', fechar: 'Cerrar', ver: 'ver en actualizaciones',
        recargaSemMotivo: 'Hay una actualización importante del portal. Recargue para continuar.' },
};

// O rótulo diz QUE TIPO DE TRABALHO entrou, não que "o portal foi atualizado"
// (Valdair, 21/09: a vitrine tem que mostrar trabalho real). Sem tipo, cai no
// rótulo genérico de TEXTOS.msg.
const ROTULO_TIPO: Record<string, { pt: string; en: string; es: string }> = {
  novo:      { pt: 'Novo no portal', en: 'New in the portal', es: 'Nuevo en el portal' },
  melhoria:  { pt: 'Melhoria entregue', en: 'Improvement shipped', es: 'Mejora entregada' },
  correcao:  { pt: 'Conserto feito', en: 'Fix shipped', es: 'Arreglo hecho' },
  seguranca: { pt: 'Segurança reforçada', en: 'Security tightened', es: 'Seguridad reforzada' },
};

const entrada = keyframes`
  from { opacity: 0; transform: translate(-50%, -12px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
`;
const escoa = keyframes`
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
`;

const Pilula = styled.div<{ $alerta: boolean }>`
  position: fixed;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: min(640px, calc(100vw - 24px));
  padding: 7px 8px 9px 14px;
  border-radius: 999px;
  background: #fff;
  color: ${p => (p.$alerta ? '#A04E3A' : theme.colors.primary)};
  border: 1px solid ${p => (p.$alerta ? 'rgba(160, 78, 58, 0.35)' : 'rgba(146, 86, 62, 0.25)')};
  box-shadow: 0 8px 24px rgba(146, 86, 62, 0.18);
  font-family: 'Satoshi', -apple-system, sans-serif;
  font-size: 13px;
  font-weight: 500;
  animation: ${entrada} 260ms ease-out;

  /* o aviso não recebe clique: ninguém perde um clique por causa dele */
  pointer-events: none;
  button { pointer-events: auto; }

  > svg { flex: none; }
  .txt { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .txt em { font-style: normal; font-weight: 400; color: ${theme.colors.textSecondary}; }
`;

// Barrinha que escoa: mostra que o aviso vai sumir sozinho. É animação de CSS —
// não existe contador redesenhando a tela de segundo em segundo.
const Barra = styled.span<{ $ms: number; $pausado: boolean }>`
  position: absolute;
  left: 14px;
  right: 10px;
  bottom: 3px;
  height: 2px;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.28;
  transform-origin: left center;
  animation: ${escoa} ${p => p.$ms}ms linear forwards;
  ${p => p.$pausado && css`animation-play-state: paused;`}
`;

const LinkVer = styled.button`
  flex: none;
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { filter: brightness(1.15); }
`;

const Botao = styled.button<{ $alerta: boolean }>`
  flex: none;
  border: none;
  border-radius: 999px;
  padding: 5px 12px;
  cursor: pointer;
  background: ${p => (p.$alerta ? '#A04E3A' : theme.colors.primary)};
  color: #fff;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  &:hover { filter: brightness(1.08); }
`;

const Fechar = styled.button`
  flex: none;
  display: flex;
  border: none;
  background: transparent;
  color: #8C8B8B;
  cursor: pointer;
  padding: 2px;
  &:hover { color: ${theme.colors.primary}; }
`;

type Fase = 'oculto' | 'esperando' | 'visivel';

/** a pessoa está no meio de algo que não pode ser interrompido? */
function ocupado(): boolean {
  if (getLiveRecordingInfo()) return true; // gravação de consulta em curso
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

const registrar = (evento: 'exibido' | 'clique' | 'recarregou' | 'fechou', versao: string | null, numero: number | null) => {
  supabase.rpc('registrar_evento_atualizacao', { p_evento: evento, p_versao: versao, p_numero: numero })
    .then(({ error }) => { if (error) console.warn('[atualizacoes] evento não registrado:', error.message); });
};

export default function NovaVersaoAviso() {
  const { i18n } = useTranslation();
  const { publicada, novaVersao, chave, exigeRecarga: exigeRecargaDaVersao } = useVersaoPortal();
  const { profile } = useAuth();
  const navigate = useNavigate();

  const [fase, setFase] = useState<Fase>('oculto');
  const [ciclo, setCiclo] = useState(0);       // reinicia a animação da barrinha
  const [pausado, setPausado] = useState(false);
  const [silencioAte, setSilencioAte] = useState(0);

  const chaveVista = useRef('');
  const relogio = useRef<number | null>(null);
  const restanteMs = useRef(0);
  const comecouEm = useRef(0);
  const pausouEm = useRef(0);
  const apareceuEm = useRef(0);   // inicio da janela de uma hora

  // Duas fontes, com papéis diferentes:
  //  • versao.json (useVersaoPortal): serve para o caso ALERTA — a aba velha que
  //    faz estrago, quando o deploy pediu recarga na nota da versão. Desde 28/09
  //    o pedido também alcança quem abriu a aba ANTES dele, e não morre no
  //    deploy seguinte (recarregar_se_anterior_a);
  //  • novidade_da_hora (banco, migration 254): a vitrine do trabalho, uma por
  //    hora, a mesma durante a hora inteira, da mais antiga não vista para a
  //    mais recente. É o modo normal.
  // 20/10 (Valdair: "fica fazendo muito spam"): a vitrine saiu do topo. A novidade é o número ao lado de
  // "Atualizações" no menu (e no rodapé dele); o topo fica só para o caso grave, a aba velha que pede recarga.
  const { novidade } = useNovidadeDaHora(false);

  // `precisaRecarregar` (no hook) responde pelas duas portas: o pedido do deploy
  // da vez e o pedido que ficou de pé para abas anteriores a ele.
  const exigeRecarga = exigeRecargaDaVersao && novaVersao;
  const interno = publicada?.tipo === 'interno';
  const tipoAviso = exigeRecarga ? (publicada?.tipo || '') : (novidade?.tipo || '');
  const duracao = DURACAO_MS[tipoAviso] || DURACAO_PADRAO_MS;
  // a página Atualizações é só de admin (AdminProtectedRoute manda provider pro /doctor)
  const podeVer = profile?.role === 'admin';

  // o que a frase diz, venha de onde vier
  const versaoAviso = exigeRecarga ? (publicada?.versao ?? null) : (novidade?.versao ?? null);
  const numeroAviso = exigeRecarga ? (publicada?.numero ?? null) : (novidade?.numero ?? null);
  // chave do ciclo: troca quando muda a novidade (ou a versão, no modo alerta)
  const chaveAviso = exigeRecarga ? (chave || '') : (novidade?.versao || '');

  const limparRelogio = () => {
    if (relogio.current) { window.clearTimeout(relogio.current); relogio.current = null; }
  };

  // ---- 1) versão nova detectada: prepara, mas não mostra ainda ----
  useEffect(() => {
    if (!chaveAviso) return;
    if (exigeRecarga && (!publicada || interno)) return;
    if (chaveAviso === chaveVista.current) return;   // esta já está na tela
    chaveVista.current = chaveAviso;
    limparRelogio();
    setPausado(false);
    setFase('esperando');
  }, [chaveAviso, exigeRecarga, publicada, interno]);

  // ---- troca de tela: a carga do portal conta como a primeira ----
  const location = useLocation();
  const ultimaTroca = useRef(Date.now());
  const [trocaTick, setTrocaTick] = useState(0);
  useEffect(() => {
    ultimaTroca.current = Date.now();
    setTrocaTick(t => t + 1);
  }, [location.pathname]);

  // ---- 2) espera o melhor momento para aparecer ----
  // O melhor momento é logo depois de abrir uma página: a pessoa está atenta e
  // ainda não mergulhou em nada. Quem fica muito tempo numa tela só não fica sem
  // a notícia: depois de MESMA_TELA_MS, o primeiro movimento serve.
  useEffect(() => {
    if (fase !== 'esperando') return;
    let vivo = true;
    let atrasoId = 0;
    let limiteId = 0;

    const mostrar = () => {
      if (!vivo) return;
      if (ocupado()) { atrasoId = window.setTimeout(mostrar, RECHECA_OCUPADO_MS); return; }
      soltarEscuta();
      window.clearTimeout(limiteId);
      restanteMs.current = duracao;
      apareceuEm.current = Date.now();
      setCiclo(c => c + 1);
      setFase('visivel');
    };
    const aoMexer = () => {
      window.clearTimeout(atrasoId);
      atrasoId = window.setTimeout(mostrar, ESPERA_POS_MOVIMENTO_MS);
    };
    const eventos: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'wheel', 'scroll', 'click', 'touchstart'];
    const soltarEscuta = () => eventos.forEach(e => window.removeEventListener(e, aoMexer));

    // acabou de trocar de tela (ou de chegar ao portal)? este é o momento
    if (Date.now() - ultimaTroca.current < JANELA_TROCA_MS) {
      atrasoId = window.setTimeout(mostrar, ESPERA_POS_TROCA_MS);
    }
    // senão, espera a próxima troca (este efeito roda de novo a cada troca);
    // e se a pessoa ficar muito tempo na mesma tela, o primeiro movimento serve
    limiteId = window.setTimeout(() => {
      eventos.forEach(e => window.addEventListener(e, aoMexer, { passive: true }));
    }, MESMA_TELA_MS);

    return () => { vivo = false; soltarEscuta(); window.clearTimeout(atrasoId); window.clearTimeout(limiteId); };
  }, [fase, duracao, trocaTick]);

  // ---- 3) na tela: conta o tempo (quando é só informação) ----
  const agendarSaida = useCallback((ms: number) => {
    if (relogio.current) { window.clearTimeout(relogio.current); relogio.current = null; }
    comecouEm.current = Date.now();
    relogio.current = window.setTimeout(() => { setFase('oculto'); }, ms);
  }, []);

  useEffect(() => {
    if (fase !== 'visivel') { limparRelogio(); return; }
    if (exigeRecarga) return;         // pediu recarga: fica até alguém decidir
    if (pausado) return;
    // vitrine: a entrega da vez fica até VIRAR A HORA (Valdair, 21/09: "cada uma
    // fica até uma hora antes da outra"). Não trava nada: o aviso não recebe
    // clique, só o link. Na hora seguinte entra a próxima entrega da vez.
    agendarSaida(exigeRecarga ? (restanteMs.current || duracao) : msAteVirarAHora());
    return limparRelogio;
  }, [fase, exigeRecarga, pausado, duracao, agendarSaida, ciclo]);

  // registra UMA vez por versão que a notícia apareceu (é o que vai dizer se 30 s serve)
  const jaRegistrado = useRef('');
  useEffect(() => {
    if (fase !== 'visivel' || !chaveAviso || jaRegistrado.current === chaveAviso) return;
    jaRegistrado.current = chaveAviso;
    // no modo normal este registro é o que PRENDE a novidade nesta hora: a
    // função do banco devolve a mesma enquanto existir um 'exibido' na hora.
    registrar('exibido', versaoAviso, numeroAviso);
  }, [fase, chaveAviso, versaoAviso, numeroAviso]);

  // ---- 4) pausa: mouse em cima, janela atrás, aba escondida ----
  const pausar = useCallback(() => {
    if (pausado || fase !== 'visivel') return;
    if (relogio.current) { window.clearTimeout(relogio.current); relogio.current = null; }
    restanteMs.current = Math.max(1_000, restanteMs.current - (Date.now() - comecouEm.current));
    pausouEm.current = Date.now();
    setPausado(true);
  }, [pausado, fase]);

  const voltar = useCallback(() => {
    if (!pausado) return;
    // sumiu por um tempão? a notícia recomeça: ela volta e ainda está lá
    if (Date.now() - pausouEm.current > AUSENCIA_REINICIA_MS) {
      restanteMs.current = duracao;
      setCiclo(c => c + 1);
    }
    setPausado(false);
  }, [pausado, duracao]);

  useEffect(() => {
    // a pausa só existia para a contagem curta; a vitrine fica a hora inteira
    if (fase !== 'visivel' || !exigeRecarga) return;
    const aoTrocarVisibilidade = () => (document.visibilityState === 'visible' ? voltar() : pausar());
    window.addEventListener('blur', pausar);
    window.addEventListener('focus', voltar);
    document.addEventListener('visibilitychange', aoTrocarVisibilidade);
    return () => {
      window.removeEventListener('blur', pausar);
      window.removeEventListener('focus', voltar);
      document.removeEventListener('visibilitychange', aoTrocarVisibilidade);
    };
  }, [fase, exigeRecarga, pausar, voltar]);

  const irParaAtualizacoes = () => {
    registrar('clique', versaoAviso, numeroAviso);
    supabase.rpc('registrar_visita_atualizacoes', {
      p_origem: 'pilula',
      p_versao: versaoAviso,
      p_numero: numeroAviso,
    }).then(({ error }) => { if (error) console.warn('[atualizacoes] clique não registrado:', error.message); });
    setFase('oculto');
    navigate('/admin/atualizacoes');
  };

  const recarregar = () => {
    registrar('recarregou', versaoAviso, numeroAviso);
    window.location.reload();
  };

  if (!exigeRecarga && !novidade) return null;
  if (exigeRecarga && (!publicada || Date.now() < silencioAte)) return null;
  if (fase !== 'visivel') return null;

  const lang = (i18n.language || 'en').slice(0, 2) as keyof typeof TEXTOS;
  const t = TEXTOS[lang] || TEXTOS.en;

  const nota = exigeRecarga
    ? ((lang === 'pt' ? publicada?.notas.pt : publicada?.notas.en) || [])[0]
    : (lang === 'pt' ? novidade?.notaPt : novidade?.notaEn) || novidade?.notaEn || '';

  // No modo recarga o texto é o motivo DAQUELE pedido — o da nota de hoje, se
  // foi hoje que pediram, ou o motivo herdado junto com o marco. Nunca a nota do
  // dia: para uma aba velha, ler a novidade de uma versão sem relação com o
  // pedido parece defeito (Gerente, 28/09). Sem motivo guardado, frase genérica.
  const detalhe = exigeRecarga ? motivoDaRecarga(publicada, t.recargaSemMotivo) : nota;
  const rotuloTrabalho = ROTULO_TIPO[tipoAviso]?.[lang] || t.msg;

  return (
    <Pilula
      role="status"
      aria-live="polite"
      $alerta={exigeRecarga}
      onMouseEnter={pausar}
      onMouseLeave={voltar}
    >
      {exigeRecarga ? <AlertTriangle size={14} /> : <RefreshCw size={14} />}
      <span className="txt" title={detalhe}>
        {exigeRecarga ? t.aviso : rotuloTrabalho}{detalhe ? <em> · {detalhe}</em> : null}
      </span>
      {podeVer && <LinkVer type="button" onClick={irParaAtualizacoes}>{t.ver}</LinkVer>}
      {!exigeRecarga && (
        // X de fechar (22/09): a entrega da vez fica a hora inteira, então a
        // pessoa precisa poder dispensar. Fechada, não volta nesta hora (o banco
        // sabe pelo evento 'fechou', então nem recarregando a página).
        <Fechar type="button" aria-label={t.fechar} title={t.fechar}
          onClick={() => { registrar('fechou', versaoAviso, numeroAviso); setFase('oculto'); }}>
          <X size={14} />
        </Fechar>
      )}
      {exigeRecarga && (
        <>
          <Botao type="button" $alerta onClick={recarregar}>{t.btn}</Botao>
          <Fechar type="button" aria-label={t.fechar} onClick={() => { setSilencioAte(Date.now() + SILENCIO_MS); setFase('oculto'); }}>
            <X size={14} />
          </Fechar>
        </>
      )}

    </Pilula>
  );
}
